import { BaseSyntheticEvent, useEffect, useState } from 'react';
import axios from 'axios';
import './UserProfile.scss';
import { Button, Card, FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { AuthHeader } from '../../../utils/helpers/auth-headers';
import { AlertSeverity } from '../../../utils/enum/Alerts';
import { setAlertData } from '../../../stores/alertStore';
import { setConnectedUser } from '../../../stores/authenticationStore';
import { PageRowsOptions } from '../../../models/DataGridConf';

function UserEditPrefs({ connectedUser }: any) {
  const { t } = useTranslation();
  const initError = {
    langError: false,
    langHelper: '',
    nbRowError: false,
    nbRowHelper: ''
  };
  const [hasError, setHasError] = useState(initError);

  const dispatch = useDispatch();

  const nbRowOptions = PageRowsOptions;

  const [userPrefs, setUserPrefs] = useState({
    lang: 'fr',
    nbRow: 5
  });

  useEffect(() => {
    if (connectedUser.prefs) {
      if (connectedUser.prefs.lang && connectedUser.prefs.nbRow) {
        setUserPrefs({
          lang: connectedUser.prefs.lang,
          nbRow: connectedUser.prefs.nbRow
        });
      } else if (connectedUser.prefs.lang && !connectedUser.prefs.nbRow) {
        setUserPrefs({
          lang: connectedUser.prefs.lang,
          nbRow: 5
        });
      } else if (!connectedUser.prefs.lang && connectedUser.prefs.nbRow) {
        setUserPrefs({
          lang: 'fr',
          nbRow: connectedUser.prefs.nbRow
        });
      }
    }
  }, [connectedUser]);

  const handleChange = (event: any) => {
    setUserPrefs({ ...userPrefs, [event.target.name]: event.target.value });
  };

  const updatePrefs = () => {
    const modifyPrefs = {
      prefs: userPrefs
    };
    axios.post(`${window._env_.REACT_APP_API_URI}/user/prefs`, modifyPrefs, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then((response) => {
        dispatch(setAlertData({
          message: t('UserProfile.updateSuccess'),
          severity: AlertSeverity.SUCCESS,
          open: true,
        }));
        dispatch(setConnectedUser({ connectedUser: response.data.data }));
      })
      .catch((error) => {
        if (error.response) {
          const err = error.response;
          console.error(err);
          dispatch(setAlertData({
            message: err.data.message,
            severity: AlertSeverity.ERROR,
            open: true,
          }));
        }
        console.error(error);
      });
  };

  const onUpdatePrefs = (event: BaseSyntheticEvent) => {
    event.preventDefault();
    const tempError = { ...initError };
    if (userPrefs.lang === '') {
      tempError.langError = true;
      tempError.langHelper = 'UserProfile.langRequired';
    }
    if (!userPrefs.nbRow) {
      tempError.nbRowError = true;
      tempError.nbRowHelper = 'UserProfile.nbRowRequired';
    }
    if (userPrefs.nbRow && userPrefs.nbRow <= 0) {
      tempError.nbRowError = true;
      tempError.nbRowHelper = 'UserProfile.nbRow0';
    }
    setHasError(tempError);
    if (_.isEqual(tempError, initError)) {
      updatePrefs();
    }
  };

  return (
    <Card>
      <div className="card-title">
        <FontAwesomeIcon className="title-icon" icon={faEdit} />&nbsp;
        {t('UserProfile.updateMyPrefs')}
      </div>
      <div>
        <form onSubmit={onUpdatePrefs} className="form">
          <FormControl fullWidth error={hasError.langError} required>
            <InputLabel id="lang-select-label">{ t('UserProfile.prefs.lang') }</InputLabel>
            <Select
              labelId="lang-select-label"
              id="lang-simple-select"
              value={userPrefs.lang}
              label={t('UserProfile.prefs.lang')}
              name="lang"
              onChange={handleChange}
              required
            >
              <MenuItem value="fr">{ t('UserProfile.fr') }</MenuItem>
              <MenuItem value="en">{ t('UserProfile.en') }</MenuItem>
            </Select>
            <FormHelperText>{hasError.langError ? t(hasError.langHelper) : ''}</FormHelperText>
          </FormControl>
          <FormControl fullWidth error={hasError.nbRowError} required>
            <InputLabel id="nbRow-select-label">{ t('UserProfile.prefs.nbRow') }</InputLabel>
            <Select
              labelId="nbRow-select-label"
              id="nbRow-simple-select"
              value={userPrefs.nbRow}
              label={t('UserProfile.prefs.nbRow')}
              name="nbRow"
              onChange={handleChange}
              required
            >
              {nbRowOptions.map((rowOption: number) => (
                <MenuItem key={rowOption} value={rowOption}>{rowOption}</MenuItem>
              ))}
            </Select>
            <FormHelperText>{hasError.nbRowError ? t(hasError.nbRowHelper) : ''}</FormHelperText>
          </FormControl>
          <Button fullWidth type="submit" value="submit" variant="contained">{t('UserProfile.submit')}</Button>
        </form>
      </div>
    </Card>
  );
}

UserEditPrefs.propTypes = {
  connectedUser: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    prefs: PropTypes.shape({
      lang: PropTypes.string,
      nbRow: PropTypes.number,
    })
  }).isRequired
};

export default UserEditPrefs;
